/*
 * Daydream assistant — loader and entry-point wiring.
 *
 * Loaded (deferred) by layout/theme.liquid only when Theme settings > Daydream
 * is enabled with an API key; the key arrives on this script tag's
 * data-daydream-api-key attribute.
 *
 * - The vendor script is injected async, so it never blocks rendering.
 * - Entry points (snippets/daydream-entry.liquid) stay hidden until init()
 *   resolves; html.daydream-ready reveals them. If the vendor script cannot be
 *   reached or init() rejects, nothing is revealed and the page is unchanged.
 * - One delegated click listener serves every entry point, including ones the
 *   theme editor or AJAX renders later.
 */
(function () {
  'use strict';

  var SDK_URL = 'https://cdn.dahlialabs.dev/sdk/daydream.js';
  var READY_CLASS = 'daydream-ready';
  var root = document.documentElement;

  if (window.__daydreamEntryLoaded) return;
  window.__daydreamEntryLoaded = true;

  var configScript = document.querySelector('script[data-daydream-api-key]');
  var apiKey = configScript ? (configScript.getAttribute('data-daydream-api-key') || '').trim() : '';
  if (!apiKey) return;

  var assistant = null;

  function report(error) {
    if (window.console && typeof window.console.warn === 'function') {
      window.console.warn('[Daydream]', (error && error.code) || '', (error && error.message) || error || '');
    }
  }

  function fail(error) {
    assistant = null;
    root.classList.remove(READY_CLASS);
    report(error);
  }

  // The theme's collapsibles (theme.js) fix an open list's height in px when it opens.
  // A list opened before the assistant was ready was measured without its entry point,
  // so revealing the entry would leave it cut off. Re-measure every open list that
  // holds an entry point, innermost first, so each parent includes its child's new height.
  function refitOpenCollapsibles() {
    var open = [];
    document.querySelectorAll('[data-daydream-entry]').forEach(function (entry) {
      var el = entry.parentElement ? entry.parentElement.closest('.collapsible-content.is-open') : null;
      while (el) {
        if (open.indexOf(el) === -1) open.push(el);
        el = el.parentElement ? el.parentElement.closest('.collapsible-content.is-open') : null;
      }
    });
    function depth(el) {
      var d = 0;
      while ((el = el.parentElement)) d++;
      return d;
    }
    open.sort(function (a, b) {
      return depth(b) - depth(a);
    });
    open.forEach(function (el) {
      var inner = el.querySelector('.collapsible-content__inner');
      if (!inner || !el.style.height || el.style.height === 'auto') return;
      el.style.height = inner.offsetHeight + 'px';
    });
  }

  // The desktop nav's STYLE ME sits out of flow after the last link (custom-nav-styles.css),
  // so it never adds a nav row. Here it steps down until it ends before the navigation area
  // does (where Client Services starts): compact, then icon only, then the top-level links
  // sit a little closer, and only as a last resort does it go back into the row.
  // Each step is [class, applied to the item (false) or to the nav list (true)].
  var NAV_FIT_STEPS = [
    ['dd-fit--compact', false],
    ['dd-fit--icon', false],
    ['dd-fit--tight', true],
    ['dd-fit--inline', false]
  ];
  function fitNavEntry() {
    document.querySelectorAll('.dd-entry-item--nav').forEach(function (item) {
      var list = item.parentElement;
      var area = item.closest('.header-item--navigation') || list;
      NAV_FIT_STEPS.forEach(function (step) {
        (step[1] ? list : item).classList.remove(step[0]);
      });
      item.style.marginTop = '';
      if (!area || !item.offsetParent) return;
      var limit = area.getBoundingClientRect().right;
      for (var i = 0; i < NAV_FIT_STEPS.length; i++) {
        if (item.getBoundingClientRect().right <= limit) break;
        (NAV_FIT_STEPS[i][1] ? list : item).classList.add(NAV_FIT_STEPS[i][0]);
      }
      // Out of flow it is top-aligned with its row; centre it on the link before it.
      var link = item.previousElementSibling && item.previousElementSibling.querySelector('.site-nav__link');
      if (!link || item.classList.contains('dd-fit--inline')) return;
      var a = link.getBoundingClientRect();
      var b = item.getBoundingClientRect();
      item.style.marginTop = Math.round(a.top + a.height / 2 - (b.top + b.height / 2)) + 'px';
    });
  }

  var navFitWatching = false;
  function watchNavEntry() {
    fitNavEntry();
    if (navFitWatching) return;
    navFitWatching = true;
    var frame = null;
    function schedule() {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        fitNavEntry();
      });
    }
    window.addEventListener('resize', schedule);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
    document.addEventListener('shopify:section:load', schedule);
  }

  function init() {
    if (!window.Daydream || typeof window.Daydream.init !== 'function') {
      fail({ code: 'NOT_LOADED', message: 'Daydream SDK unavailable' });
      return;
    }
    try {
      window.Daydream.init({ apiKey: apiKey })
        .then(function (instance) {
          if (!instance || !instance.modal || typeof instance.modal.open !== 'function') {
            fail({ code: 'INIT_FAILED', message: 'Daydream instance has no modal' });
            return;
          }
          assistant = instance;
          root.classList.add(READY_CLASS);
          refitOpenCollapsibles();
          watchNavEntry();
        })
        .catch(fail);
    } catch (error) {
      fail(error);
    }
  }

  function loadSdk() {
    if (window.Daydream) {
      init();
      return;
    }
    var script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = init;
    script.onerror = function () {
      fail({ code: 'NETWORK_ERROR', message: 'Daydream SDK could not be loaded' });
    };
    document.head.appendChild(script);
  }

  // An entry point inside the menu drawer closes the drawer first and opens the
  // assistant once the drawer has finished closing (the theme returns focus to
  // the menu button at that point, which would otherwise pull focus out of the
  // assistant). Returns true when the drawer was open.
  var DRAWER_CLOSE_MS = 550;
  function closeNavDrawer(control) {
    var drawer = control.closest('#NavDrawer');
    if (!drawer || !root.classList.contains('js-drawer-open')) return false;
    var closeButton = drawer.querySelector('.js-drawer-close');
    if (!closeButton) return false;
    closeButton.click();
    return true;
  }

  // If the assistant refuses the options for an entry point (for example the PDP_MLT
  // slot opened without a productUrl, so that it starts blank), open it in its default
  // state instead, so the click still gives the shopper a blank conversation.
  function openDefault(error) {
    report(error);
    if (!assistant) return;
    try {
      assistant.modal.open();
    } catch (fallbackError) {
      report(fallbackError);
    }
  }

  function openAssistant(options) {
    if (!assistant) return;
    try {
      var result = assistant.modal.open(options);
      if (result && typeof result.then === 'function') result.then(null, openDefault);
    } catch (error) {
      openDefault(error);
    }
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    var control = target && typeof target.closest === 'function' ? target.closest('[data-daydream-open]') : null;
    if (!control) return;
    event.preventDefault();
    if (!assistant) return;

    var options = { slot: control.getAttribute('data-daydream-slot') || 'TOP_NAV' };
    var productUrl = control.getAttribute('data-daydream-product-url');
    if (productUrl) options.productUrl = productUrl;

    if (closeNavDrawer(control)) {
      window.setTimeout(function () {
        openAssistant(options);
      }, DRAWER_CLOSE_MS);
      return;
    }
    openAssistant(options);
  });

  loadSdk();
})();
